import { useEffect, useRef } from 'react';
import { useTheme } from '@/components/theme-provider';

interface NetworkParticlesProps {
  className?: string;
  colorHex?: string;
  particleCount?: number;
}

const hexToRgb = (hex: string) => {
  let r = 0, g = 0, b = 0;
  hex = hex.replace('#', '');
  if (hex.length === 3) {
    r = parseInt(hex.charAt(0) + hex.charAt(0), 16);
    g = parseInt(hex.charAt(1) + hex.charAt(1), 16);
    b = parseInt(hex.charAt(2) + hex.charAt(2), 16);
  } else if (hex.length === 6) {
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  }
  return `${r}, ${g}, ${b}`;
};

export default function NetworkParticles({ className = '', colorHex, particleCount = 40 }: NetworkParticlesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { theme } = useTheme();

  const baseColor = colorHex ? colorHex : (theme === 'dark' ? '#ffffff' : '#13175b');
  const rgbColor = hexToRgb(baseColor);
  const isColorWhite = baseColor.toLowerCase() === '#ffffff' || baseColor.toLowerCase() === '#fff';
  const blendClass = (theme === 'dark' || isColorWhite) ? 'mix-blend-screen' : 'mix-blend-normal';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let particles: any[] = [];
    let animationFrameId: number;

    const init = () => {
      // Setup canvas dimensions to match parent container
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      } else {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
      
      // Calculate responsive particle count based on area
      const area = canvas.width * canvas.height;
      // Denser particles (divisor 10000 instead of 20000)
      const count = Math.min(Math.floor((area / 10000) * (particleCount / 40)), 250);
      
      particles = [];
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.4, // slow speed
          vy: (Math.random() - 0.5) * 0.4,
          radius: Math.random() * 1.5 + 0.8
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = theme === 'dark' || isColorWhite ? `rgba(${rgbColor}, 0.5)` : `rgba(${rgbColor}, 0.75)`;
        ctx.fill();

        // Draw connecting lines
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 140) {
            ctx.beginPath();
            const maxOpacity = theme === 'dark' || isColorWhite ? 0.20 : 0.35;
            ctx.strokeStyle = `rgba(${rgbColor}, ${maxOpacity * (1 - dist / 140)})`;
            ctx.lineWidth = 1;
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(draw);
    };

    init();
    draw();

    // Debounced resize handler
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        init();
      }, 200);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      cancelAnimationFrame(animationFrameId);
    };
  }, [theme, colorHex, particleCount, baseColor, rgbColor, isColorWhite]);

  return (
    <canvas 
      ref={canvasRef} 
      className={`absolute inset-0 pointer-events-none z-0 ${blendClass} opacity-80 ${className}`}
    />
  );
}
